import React from "react"
import { COLORS, TYPOGRAPHY, SPACING, BORDER_RADIUS } from "../constants/theme"

const PaginationNew = ({ page, totalPages, total, limit, onPageChange, onLimitChange }) => {
	const getPageNumbers = () => {
		const delta = 1
		const pages = []
		const start = Math.max(1, page - delta)
		const end = Math.min(totalPages, page + delta)

		if (start > 1) {
			pages.push(1)
			if (start > 2) {
				pages.push("...")
			}
		}

		for (let i = start; i <= end; i++) {
			pages.push(i)
		}

		if (end < totalPages) {
			if (end < totalPages - 1) {
				pages.push("...")
			}
			pages.push(totalPages)
		}

		return pages
	}

	const pageNumbers = getPageNumbers()

	const containerStyle = {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		flexWrap: "wrap",
		gap: SPACING.md,
		marginTop: SPACING.lg,
		padding: `${SPACING.md} 0`,
		fontFamily: TYPOGRAPHY.fontFamily,
	}

	const paginationControlsStyle = {
		display: "flex",
		alignItems: "center",
		gap: SPACING.xs,
		flexWrap: "wrap",
	}

	const pageButtonStyle = (isActive = false, isDisabled = false, isEllipsis = false) => ({
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minWidth: isEllipsis ? "auto" : "31px",
		height: "31px",
		padding: isEllipsis ? `0 ${SPACING.xs}` : `0 ${SPACING.sm}`,
		borderRadius: BORDER_RADIUS.md,
		border: isActive ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.borderLight}`,
		backgroundColor: isActive ? COLORS.primary : COLORS.white,
		color: isActive ? COLORS.white : isDisabled ? COLORS.gray400 : COLORS.gray700,
		fontSize: TYPOGRAPHY.fontSize.sm,
		fontWeight: isActive ? TYPOGRAPHY.fontWeight.semibold : TYPOGRAPHY.fontWeight.normal,
		cursor: isDisabled || isEllipsis ? "default" : "pointer",
		transition: "all 0.2s ease",
		opacity: isDisabled ? 0.5 : 1,
		fontFamily: TYPOGRAPHY.fontFamily,
		outline: "none",
		boxShadow: isActive ? `0 2px 4px ${COLORS.primary}33` : "none",
	})

	const navButtonStyle = (isDisabled = false) => ({
		display: "flex",
		alignItems: "center",
		gap: SPACING.xs,
		height: "31px",
		padding: `0 ${SPACING.md}`,
		borderRadius: BORDER_RADIUS.md,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.white,
		color: isDisabled ? COLORS.gray400 : COLORS.gray700,
		fontSize: TYPOGRAPHY.fontSize.sm,
		fontWeight: TYPOGRAPHY.fontWeight.medium,
		cursor: isDisabled ? "not-allowed" : "pointer",
		transition: "all 0.2s ease",
		opacity: isDisabled ? 0.5 : 1,
		fontFamily: TYPOGRAPHY.fontFamily,
		outline: "none",
		whiteSpace: "nowrap",
	})

	const infoStyle = {
		fontSize: TYPOGRAPHY.fontSize.sm,
		color: COLORS.gray600,
		fontFamily: TYPOGRAPHY.fontFamily,
		display: "flex",
		alignItems: "center",
		gap: SPACING.xs,
	}

	const selectStyle = {
		height: "31px",
		padding: `0 ${SPACING.md}`,
		borderRadius: BORDER_RADIUS.md,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.white,
		color: COLORS.gray700,
		fontSize: TYPOGRAPHY.fontSize.sm,
		fontFamily: TYPOGRAPHY.fontFamily,
		cursor: "pointer",
		outline: "none",
		minWidth: "120px",
	}

	const separatorStyle = {
		height: "20px",
		width: "1px",
		backgroundColor: COLORS.borderLight,
		margin: `0 ${SPACING.sm}`,
	}

	const handlePageClick = (pageNumber) => {
		if (pageNumber !== "..." && pageNumber !== page) {
			onPageChange(pageNumber)
		}
	}

	const handleMouseEnter = (e, isDisabled, isActive) => {
		if (!isDisabled && !isActive) {
			e.currentTarget.style.backgroundColor = COLORS.gray50
			e.currentTarget.style.borderColor = COLORS.gray300
			e.currentTarget.style.transform = "translateY(-1px)"
		}
	}

	const handleMouseLeave = (e, isDisabled, isActive) => {
		if (!isDisabled && !isActive) {
			e.currentTarget.style.backgroundColor = COLORS.white
			e.currentTarget.style.borderColor = COLORS.borderLight
			e.currentTarget.style.transform = "translateY(0)"
		}
	}

	const startRecord = (page - 1) * limit + 1
	const endRecord = Math.min(page * limit, total)
	const isMobile = typeof window !== "undefined" && window.innerWidth <= 768

	return (
		<div style={containerStyle}>
			<div style={infoStyle}>
				<span>
					{isMobile ? (
						<>
							หน้า {page} / {totalPages} • {total.toLocaleString()} รายการ
						</>
					) : (
						<>
							แสดง <strong>{startRecord.toLocaleString()}</strong> - <strong>{endRecord.toLocaleString()}</strong> จากทั้งหมด{" "}
							<strong>{total.toLocaleString()}</strong> รายการ
						</>
					)}
				</span>
			</div>

			<div style={paginationControlsStyle}>
				{isMobile ? (
					<>
						<button
							onClick={() => handlePageClick(page - 1)}
							disabled={page === 1 || totalPages === 1}
							style={navButtonStyle(page === 1 || totalPages === 1)}
							title="หน้าก่อนหน้า"
							onMouseEnter={(e) => handleMouseEnter(e, page === 1 || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === 1 || totalPages === 1, false)}
						>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>

						<div
							style={{
								...infoStyle,
								padding: `0 ${SPACING.md}`,
								borderRadius: BORDER_RADIUS.md,
								background: COLORS.gray50,
								color: COLORS.gray700,
							}}
						>
							หน้า {page} / {totalPages}
						</div>

						<button
							onClick={() => handlePageClick(page + 1)}
							disabled={page === totalPages || totalPages === 1}
							style={navButtonStyle(page === totalPages || totalPages === 1)}
							title="หน้าถัดไป"
							onMouseEnter={(e) => handleMouseEnter(e, page === totalPages || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === totalPages || totalPages === 1, false)}
						>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
					</>
				) : (
					<>
						<button
							onClick={() => handlePageClick(1)}
							disabled={page === 1 || totalPages === 1}
							style={navButtonStyle(page === 1 || totalPages === 1)}
							title="หน้าแรก"
							onMouseEnter={(e) => handleMouseEnter(e, page === 1 || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === 1 || totalPages === 1, false)}
						>
							หน้าแรก
						</button>
						<button
							onClick={() => handlePageClick(page - 1)}
							disabled={page === 1 || totalPages === 1}
							style={navButtonStyle(page === 1 || totalPages === 1)}
							title="หน้าก่อนหน้า"
							onMouseEnter={(e) => handleMouseEnter(e, page === 1 || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === 1 || totalPages === 1, false)}
						>
							ก่อนหน้า
						</button>

						{pageNumbers.map((pageNum, index) => {
							const isEllipsis = pageNum === "..."
							const isActive = pageNum === page
							const isDisabled = isEllipsis
							return (
								<button
									key={`${pageNum}_${index}`}
									onClick={() => handlePageClick(pageNum)}
									disabled={isDisabled}
									style={pageButtonStyle(isActive, isDisabled, isEllipsis)}
									title={isEllipsis ? "More pages" : `หน้า ${pageNum}`}
									onMouseEnter={(e) => handleMouseEnter(e, isDisabled, isActive)}
									onMouseLeave={(e) => handleMouseLeave(e, isDisabled, isActive)}
								>
									{pageNum}
								</button>
							)
						})}

						<div style={separatorStyle} />

						<button
							onClick={() => handlePageClick(page + 1)}
							disabled={page === totalPages || totalPages === 1}
							style={navButtonStyle(page === totalPages || totalPages === 1)}
							title="หน้าถัดไป"
							onMouseEnter={(e) => handleMouseEnter(e, page === totalPages || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === totalPages || totalPages === 1, false)}
						>
							ถัดไป
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>

						<button
							onClick={() => handlePageClick(totalPages)}
							disabled={page === totalPages || totalPages === 1}
							style={navButtonStyle(page === totalPages || totalPages === 1)}
							title="หน้าสุดท้าย"
							onMouseEnter={(e) => handleMouseEnter(e, page === totalPages || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === totalPages || totalPages === 1, false)}
						>
							หน้าสุดท้าย
						</button>
					</>
				)}
			</div>

			<div style={{ display: "flex", alignItems: "center", gap: SPACING.md }}>
				<select value={limit} onChange={(e) => onLimitChange(Number(e.target.value))} style={selectStyle}>
					<option value={10}>10 รายการ</option>
					<option value={20}>20 รายการ</option>
					<option value={50}>50 รายการ</option>
					<option value={100}>100 รายการ</option>
				</select>
			</div>
		</div>
	)
}

export default PaginationNew
